/* ═══════════════════════════════════════════════════════════════════════════
   THE SURFACE SWITCHER — a signpost, and only that

   Five surfaces share this app, and four of them have no link pointing at
   them: you reach the business dashboard by typing /business, and if nobody
   tells you that, the app looks like one page that lost most of its features.
   That is a real failure of the build and this is the repair.

   It is a development aid, not a feature. It appears under `npm run dev`, and
   in a build only when the URL says ?preview=1 — so a real visitor on
   avtale.net never sees a link to the platform admin. The day there is a
   login, this file is deleted and each surface is reached by signing in to it.
   ═══════════════════════════════════════════════════════════════════════ */
import { useState } from 'react';
import { navigate } from '../router.js';
import { BUSINESSES } from '../data/catalogue.js';
import { industry } from '../industries/index.js';

/** Shown in dev, in a hash-routed preview build, or with ?preview=1 in the
    URL. Never on the real site, where each surface is reached by signing in
    and a visitor has no business being handed a link to the platform admin. */
export function visible() {
  try {
    if (import.meta.env?.DEV) return true;
    if (import.meta.env?.VITE_ROUTER === 'hash') return true;
    return new URLSearchParams(window.location.search).has('preview');
  } catch {
    return false;
  }
}

/* One business per layout, so the four page recipes are each one click away.
   Chosen from the data rather than written down, so the list cannot name an
   industry and cannot go stale when the catalogue changes. */
function samples() {
  const seen = new Set();
  const out = [];
  for (const b of BUSINESSES) {
    const ind = industry(b.industry);
    if (seen.has(ind.layout)) continue;
    seen.add(ind.layout);
    out.push({ slug: b.slug, name: b.name, layout: ind.layout, industry: ind.name });
  }
  return out;
}

export default function Surfaces({ here }) {
  const [open, setOpen] = useState(false);
  if (!visible()) return null;

  const go = to => { navigate(to); setOpen(false); };
  const is = s => here === s;

  return (
    <div className={'surfaces' + (open ? ' open' : '')}>
      <button className="surfaces-handle" onClick={() => setOpen(!open)}
              aria-expanded={open} title="Jump between the five surfaces">
        <b>{open ? '×' : '☰'}</b>
        <span>{open ? 'Close' : 'Surfaces'}</span>
      </button>

      {open && (
        <div className="surfaces-panel">
          <p className="surfaces-note">
            Five surfaces, one app. In production each is reached by signing in;
            until there is a login, this is how you get to them. It is not shown
            to visitors.
          </p>

          <h5>The platform</h5>
          <button className={'surfaces-row' + (is('customer') ? ' on' : '')} onClick={() => go('/')}>
            <b>Customer</b><i>avtale.net/</i>
            <span>The marketplace and the customer's own mirror</span>
          </button>
          <button className={'surfaces-row' + (is('business') ? ' on' : '')} onClick={() => go('/business')}>
            <b>Business</b><i>avtale.net/business</i>
            <span>The tenant's diary, customers, price list and website</span>
          </button>
          <button className={'surfaces-row' + (is('staff') ? ' on' : '')} onClick={() => go('/staff')}>
            <b>Staff</b><i>avtale.net/staff</i>
            <span>One person's day, and only their own customers</span>
          </button>
          <button className={'surfaces-row' + (is('admin') ? ' on' : '')} onClick={() => go('/admin')}>
            <b>Platform</b><i>avtale.net/admin</i>
            <span>The industry registry and every business on it</span>
          </button>

          <h5>A business's own website — one per page recipe</h5>
          {samples().map(s => (
            <button key={s.slug} className="surfaces-row" onClick={() => go('/' + s.slug)}>
              <b>{s.name}</b><i>avtale.net/{s.slug}</i>
              <span>{s.industry} · the “{s.layout}” layout</span>
            </button>
          ))}
          <p className="surfaces-note">
            Every one of the {BUSINESSES.length} businesses has a page of its own at
            its own address. These four are the four different shapes those pages
            come in.
          </p>
        </div>
      )}
    </div>
  );
}
